import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import KeyIcon from '@mui/icons-material/Key';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUsers,
  createUser,
  changePassword,
  changeRole,
  deleteUser
} from '../../services/users';
import { sendAlertTest } from '../../services/api';

const ROLE_LABELS = {
  admin: 'Администратор',
  user: 'Пользователь',
  viewer: 'Наблюдатель'
};

const ROLE_COLORS = {
  admin: 'error',
  user: 'primary',
  viewer: 'default'
};

const roleLabel = (role) => ROLE_LABELS[role] || role;

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Диалог создания пользователя
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', password: '', role: 'user' });
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  // Диалог смены пароля
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirm: '' });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Диалог смены роли
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState(null);
  const [roleValue, setRoleValue] = useState('user');
  const [roleError, setRoleError] = useState(null);
  const [roleSaving, setRoleSaving] = useState(false);

  // Диалог удаления
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Глобальное уведомление об успехе
  const [notice, setNotice] = useState(null);

  // Тест оповещений Max
  const [tgTesting, setTgTesting] = useState(false);
  const [tgNotice, setTgNotice] = useState(null); // { severity, message }

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const showNotice = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 4000);
  };

  // Отправка тестового сообщения в Max. Убеждает, что API-ключ/chat_id рабочие,
  // до того как случится реальная авария.
  const handleTestAlert = async () => {
    setTgTesting(true);
    setTgNotice(null);
    try {
      const result = await sendAlertTest();
      if (result.ok && result.delivered) {
        setTgNotice({ severity: 'success', message: 'Тестовое сообщение отправлено в Max.' });
      } else if (result.configured === false) {
        setTgNotice({
          severity: 'warning',
          message: 'Max не настроен на сервере. Укажите MAX_API_KEY и MAX_CHAT_ID в server/.env.',
        });
      } else {
        setTgNotice({
          severity: 'error',
          message: 'Не удалось отправить сообщение. Проверьте MAX_API_KEY, MAX_CHAT_ID и что бот добавлен в чат.',
        });
      }
    } catch (err) {
      setTgNotice({ severity: 'error', message: err.message || 'Ошибка запроса к серверу.' });
    } finally {
      setTgTesting(false);
      setTimeout(() => setTgNotice(null), 6000);
    }
  };

  // ===== Создание пользователя =====
  const openCreate = () => {
    setCreateForm({ username: '', password: '', role: 'user' });
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const { username, password, role } = createForm;
    if (!username || !password) {
      setCreateError('Укажите имя пользователя и пароль');
      return;
    }
    if (password.length < 6) {
      setCreateError('Пароль должен содержать не менее 6 символов');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await createUser({ username, password, role });
      setCreateOpen(false);
      showNotice(`Пользователь «${username}» создан`);
      await loadUsers();
    } catch (err) {
      setCreateError(err.message || 'Ошибка при создании пользователя');
    } finally {
      setCreating(false);
    }
  };

  // ===== Смена пароля =====
  const openPassword = (target) => {
    setPasswordTarget(target);
    setPasswordForm({ newPassword: '', confirm: '' });
    setPasswordError(null);
    setPasswordOpen(true);
  };

  const handlePassword = async () => {
    const { newPassword, confirm } = passwordForm;
    if (newPassword.length < 6) {
      setPasswordError('Пароль должен содержать не менее 6 символов');
      return;
    }
    if (newPassword !== confirm) {
      setPasswordError('Пароли не совпадают');
      return;
    }
    setPasswordSaving(true);
    setPasswordError(null);
    try {
      await changePassword(passwordTarget.id, { newPassword });
      setPasswordOpen(false);
      showNotice(`Пароль пользователя «${passwordTarget.username}» изменён`);
    } catch (err) {
      setPasswordError(err.message || 'Ошибка при смене пароля');
    } finally {
      setPasswordSaving(false);
    }
  };

  // ===== Смена роли =====
  const openRole = (target) => {
    setRoleTarget(target);
    setRoleValue(target.role);
    setRoleError(null);
    setRoleOpen(true);
  };

  const handleRole = async () => {
    setRoleSaving(true);
    setRoleError(null);
    try {
      await changeRole(roleTarget.id, roleValue);
      setRoleOpen(false);
      showNotice(`Роль пользователя «${roleTarget.username}» изменена`);
      await loadUsers();
    } catch (err) {
      setRoleError(err.message || 'Ошибка при смене роли');
    } finally {
      setRoleSaving(false);
    }
  };

  // ===== Удаление =====
  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteUser(deleteTarget.id);
      const name = deleteTarget.username;
      setDeleteTarget(null);
      showNotice(`Пользователь «${name}» удалён`);
      await loadUsers();
    } catch (err) {
      setDeleteError(err.message || 'Ошибка при удалении пользователя');
    } finally {
      setDeleting(false);
    }
  };

  const isAdmin = currentUser?.role === 'admin';

  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">Доступ к управлению пользователями разрешён только администраторам.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={loadUsers}>Повторить</Button>
      </Box>
    );
  }

  const renderActions = (u) => (
    <Stack direction="row" spacing={0.5}>
      <IconButton size="small" title="Сменить пароль" onClick={() => openPassword(u)}>
        <KeyIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" title="Изменить роль" onClick={() => openRole(u)}>
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        title="Удалить"
        color="error"
        disabled={u.id === currentUser.id}
        onClick={() => { setDeleteTarget(u); setDeleteError(null); }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Пользователи</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openCreate}>
          Добавить пользователя
        </Button>
      </Box>

      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      {/* Карточка оповещений Max (только для админов) */}
      {currentUser?.role === 'admin' && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActiveIcon color="error" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Оповещения об авариях (Max)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Бот шлёт алерт при массовом падении на PON-ветке. Настройте API-ключ и chat_id в <code>server/.env</code>.
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<NotificationsActiveIcon />}
                onClick={handleTestAlert}
                disabled={tgTesting}
              >
                {tgTesting ? <CircularProgress size={20} /> : 'Отправить тест'}
              </Button>
            </Stack>
            {tgNotice && (
              <Alert severity={tgNotice.severity} sx={{ mt: 2 }}>
                {tgNotice.message}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Десктоп: таблица */}
      {!isMobile ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Имя пользователя</TableCell>
                <TableCell>Роль</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>
                    {u.username}
                    {u.id === currentUser.id && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (вы)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={roleLabel(u.role)} color={ROLE_COLORS[u.role]} />
                  </TableCell>
                  <TableCell align="right">{renderActions(u)}</TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">Пользователи не найдены</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Мобильная версия: карточки */
        <Grid container spacing={2}>
          {users.map((u) => (
            <Grid item xs={12} key={u.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {u.username}
                        {u.id === currentUser.id && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (вы)
                          </Typography>
                        )}
                      </Typography>
                      <Chip size="small" label={roleLabel(u.role)} color={ROLE_COLORS[u.role]} sx={{ mt: 0.5 }} />
                    </Box>
                    {renderActions(u)}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ===== Диалог создания ===== */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Новый пользователь</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Имя пользователя"
              value={createForm.username}
              onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
              autoFocus
              fullWidth
            />
            <TextField
              label="Пароль"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              helperText="Минимум 6 символов"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Роль</InputLabel>
              <Select
                value={createForm.role}
                label="Роль"
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
              >
                <MenuItem value="admin">{roleLabel('admin')}</MenuItem>
                <MenuItem value="user">{roleLabel('user')}</MenuItem>
                <MenuItem value="viewer">{roleLabel('viewer')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {creating ? 'Создание...' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Диалог смены пароля ===== */}
      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Смена пароля{passwordTarget ? `: ${passwordTarget.username}` : ''}</DialogTitle>
        <DialogContent>
          {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Новый пароль"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              helperText="Минимум 6 символов"
              autoFocus
              fullWidth
            />
            <TextField
              label="Повторите пароль"
              type="password"
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordOpen(false)}>Отмена</Button>
          <Button onClick={handlePassword} variant="contained" disabled={passwordSaving}>
            {passwordSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Диалог смены роли ===== */}
      <Dialog open={roleOpen} onClose={() => setRoleOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Изменение роли{roleTarget ? `: ${roleTarget.username}` : ''}</DialogTitle>
        <DialogContent>
          {roleError && <Alert severity="error" sx={{ mb: 2 }}>{roleError}</Alert>}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Роль</InputLabel>
            <Select
              value={roleValue}
              label="Роль"
              onChange={(e) => setRoleValue(e.target.value)}
            >
              <MenuItem value="admin">{roleLabel('admin')}</MenuItem>
              <MenuItem value="user">{roleLabel('user')}</MenuItem>
              <MenuItem value="viewer">{roleLabel('viewer')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleOpen(false)}>Отмена</Button>
          <Button onClick={handleRole} variant="contained" disabled={roleSaving}>
            {roleSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Диалог подтверждения удаления ===== */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Удаление пользователя</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography>
            Удалить пользователя «{deleteTarget?.username}»? Действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Удаление...' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
